import React, { useState, useEffect, useCallback } from "react";
import {
  Coins,
  RotateCcw,
  Info,
  Play,
  Plus,
  Minus,
  X,
  ShieldAlert,
} from "lucide-react";

// ─── GAME LOGIC & CONSTANTS ─────────────────────────────────────────────

const SUITS = ["♥", "♦", "♣", "♠"];
const RANKS = [
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "10",
  "J",
  "Q",
  "K",
  "A",
];

const getDeck = () => {
  let deck = [];
  for (let suit of SUITS) {
    for (let rank of RANKS) {
      let value = parseInt(rank);
      if (["J", "Q", "K"].includes(rank)) value = 10;
      if (rank === "A") value = 11;
      deck.push({ suit, rank, value });
    }
  }
  return deck.sort(() => Math.random() - 0.5); // Simple shuffle
};

const calculateScore = (hand) => {
  let score = 0;
  let aces = 0;
  hand.forEach((card) => {
    score += card.value;
    if (card.rank === "A") aces += 1;
  });
  while (score > 21 && aces > 0) {
    score -= 10;
    aces -= 1;
  }
  return score;
};

// ─── COMPONENTS ─────────────────────────────────────────────────────────

const Card = ({ card, hidden, delay }) => {
  if (hidden) {
    return (
      <div
        className="relative w-16 h-24 sm:w-24 sm:h-36 rounded-xl border-2 border-white shadow-xl flex items-center justify-center animate-slide-in-top"
        style={{
          background:
            "repeating-linear-gradient(45deg, #ef4444 0, #ef4444 10px, #b91c1c 10px, #b91c1c 20px)",
          animationFillMode: "both",
          animationDelay: `${delay}ms`,
        }}
      >
        <ShieldAlert className="text-white/50 w-8 h-8 sm:w-12 sm:h-12" />
      </div>
    );
  }

  const isRed = card.suit === "♥" || card.suit === "♦";

  return (
    <div
      className={`relative w-16 h-24 sm:w-24 sm:h-36 bg-white rounded-xl shadow-2xl flex flex-col justify-between p-1.5 sm:p-2 animate-slide-in-top`}
      style={{
        animationFillMode: "both",
        animationDelay: `${delay}ms`,
      }}
    >
      <div
        className={`text-left leading-none ${isRed ? "text-red-600" : "text-slate-900"}`}
      >
        <div className="text-lg sm:text-2xl font-bold">{card.rank}</div>
        <div className="text-xl sm:text-3xl">{card.suit}</div>
      </div>
      <div
        className={`absolute inset-0 flex items-center justify-center opacity-10 pointer-events-none ${isRed ? "text-red-600" : "text-slate-900"}`}
      >
        <span className="text-5xl sm:text-7xl">{card.suit}</span>
      </div>
      <div
        className={`text-right leading-none rotate-180 ${isRed ? "text-red-600" : "text-slate-900"}`}
      >
        <div className="text-lg sm:text-2xl font-bold">{card.rank}</div>
        <div className="text-xl sm:text-3xl">{card.suit}</div>
      </div>
    </div>
  );
};

const TutorialModal = ({ onClose }) => (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
    onClick={onClose}
  >
    <div
      className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex justify-between items-center mb-4 border-b pb-2">
        <h2 className="text-xl font-black text-slate-800">How to Play</h2>
        <button
          onClick={onClose}
          className="text-slate-400 hover:text-slate-600 transition"
        >
          <X />
        </button>
      </div>
      <div className="space-y-3 text-sm text-slate-600 font-medium">
        <p>
          🃏 <strong className="text-slate-800">Goal:</strong> Beat the dealer's
          hand without going over 21.
        </p>
        <p>
          🔢 <strong className="text-slate-800">Values:</strong> Face cards (J,
          Q, K) are 10. Aces are 1 or 11.
        </p>
        <p>
          🎯 <strong className="text-slate-800">Blackjack:</strong> Hitting 21
          on your first two cards pays 3:2!
        </p>
        <p>
          🛑 <strong className="text-slate-800">Dealer Rules:</strong> The
          dealer must draw cards until their total is 17 or higher.
        </p>
        <p>
          👉 <strong className="text-slate-800">Hit:</strong> Take another card.{" "}
          <br />
          🤚 <strong className="text-slate-800">Stand:</strong> End your turn.
        </p>
      </div>
      <button
        onClick={onClose}
        className="mt-6 w-full py-2.5 bg-green-600 hover:bg-green-700 text-white font-bold rounded-xl transition"
      >
        Let's Play!
      </button>
    </div>
  </div>
);

// ─── MAIN COMPONENT ─────────────────────────────────────────────────────

export default function BlackJack() {
  // Lite memory caching for credits
  const [credits, setCredits] = useState(() => {
    const saved = localStorage.getItem("bj_lite_credits");
    return saved ? parseInt(saved, 10) : 1000;
  });

  const [bet, setBet] = useState(50);
  const [deck, setDeck] = useState([]);
  const [playerHand, setPlayerHand] = useState([]);
  const [dealerHand, setDealerHand] = useState([]);
  const [gameState, setGameState] = useState("betting"); // betting, playing, dealerTurn, gameOver
  const [message, setMessage] = useState("");
  const [showTutorial, setShowTutorial] = useState(false);

  // Sync credits to local storage
  useEffect(() => {
    localStorage.setItem("bj_lite_credits", credits);
  }, [credits]);

  // Adjust bet if credits drop below current bet
  useEffect(() => {
    if (credits > 0 && bet > credits) setBet(credits);
  }, [credits, bet]);

  const placeBet = () => {
    if (credits < bet || bet <= 0) return;

    setCredits((prev) => prev - bet);
    const newDeck = getDeck();

    // Deal initial cards
    const pHand = [newDeck.pop(), newDeck.pop()];
    const dHand = [newDeck.pop(), newDeck.pop()];

    setDeck(newDeck);
    setPlayerHand(pHand);
    setDealerHand(dHand);
    setMessage("");

    const pScore = calculateScore(pHand);
    const dScore = calculateScore(dHand);

    // Instant Blackjack check
    if (pScore === 21) {
      if (dScore === 21) {
        endGame("Push! Both have Blackjack.", "push", dHand);
      } else {
        endGame("Blackjack! You win 3:2!", "blackjack", dHand);
      }
    } else {
      setGameState("playing");
    }
  };

  const hit = () => {
    if (gameState !== "playing") return;
    const currentDeck = [...deck];
    const newCard = currentDeck.pop();
    const newHand = [...playerHand, newCard];

    setDeck(currentDeck);
    setPlayerHand(newHand);

    if (calculateScore(newHand) > 21) {
      endGame("Bust! You went over 21.", "lose", dealerHand);
    }
  };

  const stand = () => {
    if (gameState !== "playing") return;
    setGameState("dealerTurn");
  };

  // Dealer's turn logic
  useEffect(() => {
    if (gameState === "dealerTurn") {
      let currentDealerHand = [...dealerHand];
      let currentDeck = [...deck];
      let dScore = calculateScore(currentDealerHand);

      const playDealer = async () => {
        // Simple artificial delay for visual effect
        while (dScore < 17) {
          await new Promise((r) => setTimeout(r, 600));
          currentDealerHand.push(currentDeck.pop());
          dScore = calculateScore(currentDealerHand);
          setDealerHand([...currentDealerHand]);
          setDeck([...currentDeck]);
        }

        const pScore = calculateScore(playerHand);

        if (dScore > 21) {
          endGame("Dealer Busts! You Win!", "win", currentDealerHand);
        } else if (dScore > pScore) {
          endGame("Dealer Wins!", "lose", currentDealerHand);
        } else if (dScore < pScore) {
          endGame("You Win!", "win", currentDealerHand);
        } else {
          endGame("Push! It's a tie.", "push", currentDealerHand);
        }
      };

      playDealer();
    }
  }, [gameState]);

  const endGame = (msg, result, finalDealerHand) => {
    setGameState("gameOver");
    setMessage(msg);
    setDealerHand(finalDealerHand);

    if (result === "win") setCredits((c) => c + bet * 2);
    if (result === "blackjack") setCredits((c) => c + bet + bet * 1.5);
    if (result === "push") setCredits((c) => c + bet);
  };

  const resetGame = () => {
    if (credits === 0) {
      setCredits(1000); // Bankrupt reset
      setBet(50);
    }
    setPlayerHand([]);
    setDealerHand([]);
    setGameState("betting");
    setMessage("");
  };

  return (
    <div className="min-h-screen bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-green-700 via-green-900 to-black flex flex-col items-center justify-between font-sans overflow-hidden">
      {/* ─── INJECT CSS ANIMATIONS ─── */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
        @keyframes slideInTop {
          0% { transform: translateY(-50px) scale(0.9); opacity: 0; }
          100% { transform: translateY(0) scale(1); opacity: 1; }
        }
        .animate-slide-in-top { animation: slideInTop 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275); }
      `,
        }}
      />

      {/* ─── HEADER BAR ─── */}
      <div className="w-full bg-black/40 backdrop-blur-md border-b border-green-500/30 px-4 py-3 sm:px-8 flex justify-between items-center z-10 shadow-xl">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center shadow-lg border-2 border-green-300">
            <span className="text-white font-black text-xl">♠</span>
          </div>
          <h1 className="hidden sm:block text-xl font-black text-white tracking-widest uppercase">
            Lite Blackjack
          </h1>
        </div>

        <div className="flex items-center gap-4 sm:gap-6">
          <div className="flex items-center gap-2 bg-black/50 px-3 py-1.5 sm:px-4 sm:py-2 rounded-xl border border-white/10">
            <Coins className="text-yellow-400 w-5 h-5" />
            <span className="text-lg sm:text-xl font-bold text-yellow-400">
              {credits}
            </span>
          </div>
          <button
            onClick={() => setShowTutorial(true)}
            className="text-white/60 hover:text-white transition"
          >
            <Info size={24} />
          </button>
        </div>
      </div>

      {/* ─── GAME PLAY AREA ─── */}
      <div className="flex-1 w-full max-w-4xl flex flex-col justify-center items-center py-8 relative px-4">
        {/* Dealer Area */}
        {gameState !== "betting" && (
          <div className="flex flex-col items-center mb-8 sm:mb-16 w-full">
            <div className="flex gap-2 mb-2 items-center">
              <span className="text-white/70 text-sm font-bold uppercase tracking-widest">
                Dealer
              </span>
              {gameState === "gameOver" && (
                <span className="bg-black/50 text-white px-2 py-0.5 rounded text-xs font-bold">
                  {calculateScore(dealerHand)}
                </span>
              )}
            </div>
            <div className="flex justify-center gap-[-2rem] sm:gap-[-3rem]">
              {dealerHand.map((card, i) => (
                <div
                  key={`dealer-${i}`}
                  className={i > 0 ? "-ml-8 sm:-ml-12" : ""}
                >
                  <Card
                    card={card}
                    hidden={i === 1 && gameState === "playing"}
                    delay={i * 200}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Status Message Overlay */}
        {gameState === "gameOver" && (
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20 w-full text-center pointer-events-none">
            <h2
              className={`text-4xl sm:text-6xl font-black drop-shadow-[0_4px_4px_rgba(0,0,0,0.8)] ${message.includes("Win") || message.includes("Blackjack") ? "text-yellow-400 animate-bounce" : "text-white"}`}
            >
              {message}
            </h2>
          </div>
        )}

        {/* Player Area */}
        {gameState !== "betting" && (
          <div className="flex flex-col items-center w-full">
            <div className="flex justify-center gap-[-2rem] sm:gap-[-3rem] mb-2">
              {playerHand.map((card, i) => (
                <div
                  key={`player-${i}`}
                  className={i > 0 ? "-ml-8 sm:-ml-12" : ""}
                >
                  <Card card={card} delay={i * 200 + 400} />
                </div>
              ))}
            </div>
            <div className="flex gap-2 items-center">
              <span className="text-white/70 text-sm font-bold uppercase tracking-widest">
                You
              </span>
              <span className="bg-blue-600/80 text-white px-2 py-0.5 rounded text-xs font-bold shadow-lg">
                {calculateScore(playerHand)}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* ─── CONTROLS BOTTOM BAR ─── */}
      <div className="w-full bg-black/60 backdrop-blur-md p-6 border-t border-white/10 z-10 shadow-[0_-10px_30px_rgba(0,0,0,0.5)] flex justify-center pb-8 sm:pb-6">
        {/* Betting Phase Controls */}
        {gameState === "betting" && (
          <div className="flex flex-col items-center gap-4 w-full max-w-sm">
            {credits === 0 ? (
              <div className="text-center">
                <p className="text-red-400 font-bold mb-4">
                  Bankrupt! You lost all your credits.
                </p>
                <button
                  onClick={resetGame}
                  className="px-8 py-3 bg-white text-black font-black rounded-full flex items-center gap-2 hover:bg-gray-200 transition mx-auto"
                >
                  <RotateCcw size={20} /> Reset Bankroll
                </button>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between w-full bg-white/10 rounded-full p-2 border border-white/20">
                  <button
                    onClick={() => setBet((b) => Math.max(10, b - 10))}
                    className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition disabled:opacity-30"
                    disabled={bet <= 10}
                  >
                    <Minus size={20} />
                  </button>
                  <div className="flex flex-col items-center">
                    <span className="text-xs text-white/50 uppercase tracking-widest font-bold">
                      Your Bet
                    </span>
                    <span className="text-2xl font-black text-white">
                      {bet}
                    </span>
                  </div>
                  <button
                    onClick={() => setBet((b) => Math.min(credits, b + 10))}
                    className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition disabled:opacity-30"
                    disabled={bet >= credits}
                  >
                    <Plus size={20} />
                  </button>
                </div>

                <button
                  onClick={placeBet}
                  className="w-full py-4 bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-black text-xl rounded-full shadow-[0_0_20px_rgba(234,179,8,0.4)] hover:shadow-[0_0_30px_rgba(234,179,8,0.8)] hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2"
                >
                  <Play size={24} fill="currentColor" /> DEAL CARDS
                </button>
              </>
            )}
          </div>
        )}

        {/* Playing Phase Controls (Hit / Stand) */}
        {gameState === "playing" && (
          <div className="flex gap-4 w-full max-w-sm">
            <button
              onClick={hit}
              className="flex-1 py-4 bg-gradient-to-b from-blue-500 to-blue-700 text-white font-black text-xl rounded-2xl shadow-[0_0_20px_rgba(59,130,246,0.4)] hover:scale-105 transition-transform"
            >
              HIT
            </button>
            <button
              onClick={stand}
              className="flex-1 py-4 bg-gradient-to-b from-red-500 to-red-700 text-white font-black text-xl rounded-2xl shadow-[0_0_20px_rgba(239,68,68,0.4)] hover:scale-105 transition-transform"
            >
              STAND
            </button>
          </div>
        )}

        {/* Game Over Controls */}
        {gameState === "gameOver" && (
          <div className="flex flex-col items-center w-full max-w-sm animate-slide-in-top">
            <button
              onClick={resetGame}
              className="w-full py-4 bg-gradient-to-r from-green-400 to-green-600 text-white font-black text-xl rounded-full shadow-[0_0_20px_rgba(34,197,94,0.4)] hover:shadow-[0_0_30px_rgba(34,197,94,0.8)] hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2"
            >
              <RotateCcw size={24} /> PLAY AGAIN
            </button>
          </div>
        )}
      </div>

      {/* ─── MODALS ─── */}
      {showTutorial && <TutorialModal onClose={() => setShowTutorial(false)} />}
    </div>
  );
}
